type PortalDotEffectOptions = {
  spawnDelay?: number;
  countPerBurst?: number;
  radius?: { min: number; max: number };
  distance?: { min: number; max: number };
  duration?: number;
  color?: number;
  depth?: number;
  prewarmTime?: number;
};

export class PortalDotEffect {
  private scene: Phaser.Scene;
  private portalBurstTimer?: Phaser.Time.TimerEvent;
  private dots = new Set<Phaser.GameObjects.Rectangle>();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  start(x: number, y: number, options?: PortalDotEffectOptions) {
    this.stop();

    const {
      spawnDelay = 800,
      countPerBurst = 1,
      radius = { min: 40, max: 40 },
      distance = { min: 300, max: 400 },
      duration = 8000,
      color = 0xffffff,
      depth = 10,
      prewarmTime = 8000,
    } = options ?? {};

    const createDot = (progress = 0) => {
      const angle = Phaser.Math.FloatBetween(0, Math.PI * 2);
      const dist = Phaser.Math.FloatBetween(distance.min, distance.max);

      const targetX = x + Math.cos(angle) * dist;
      const targetY = y + Math.sin(angle) * dist;

      const size = Phaser.Math.FloatBetween(radius.min, radius.max);

      const currentX = Phaser.Math.Linear(x, targetX, progress);
      const currentY = Phaser.Math.Linear(y, targetY, progress);
      const currentAlpha = 1 - progress;
      const currentScale = Phaser.Math.Linear(0.5, 1, progress);

      const dot = this.scene.add
        .rectangle(currentX, currentY, size, size, color, currentAlpha)
        .setDepth(depth)
        .setScale(currentScale);

      this.dots.add(dot);

      this.scene.tweens.add({
        targets: dot,
        x: targetX,
        y: targetY,
        alpha: 0,
        scale: 1,
        duration: duration * (1 - progress),
        ease: "Linear",
        onComplete: () => {
          this.dots.delete(dot);
          dot.destroy();
        },
      });
    };

    const spawnBurst = () => {
      for (let i = 0; i < countPerBurst; i++) {
        createDot(0);
      }

      this.portalBurstTimer = this.scene.time.delayedCall(
        spawnDelay,
        spawnBurst,
      );
    };

    const burstCount = Math.floor(prewarmTime / spawnDelay);

    for (let burstIndex = 0; burstIndex < burstCount; burstIndex++) {
      const elapsed = prewarmTime - burstIndex * spawnDelay;
      const progress = Phaser.Math.Clamp(elapsed / duration, 0, 1);

      for (let i = 0; i < countPerBurst; i++) {
        createDot(progress);
      }
    }

    spawnBurst();
  }

  stop() {
    this.portalBurstTimer?.remove(false);
    this.portalBurstTimer = undefined;

    this.dots.forEach((dot) => {
      this.scene.tweens.killTweensOf(dot);
      dot.destroy();
    });

    this.dots.clear();
  }
}

export function applyFloatingEffect(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.Image,
  options?: {
    scaleRatio?: number;
    duration?: number;
    delay?: number;
  },
) {
  const {
    scaleRatio = 1.08,
    duration = 1400,
    delay = Phaser.Math.Between(0, 200),
  } = options ?? {};

  const baseScale = target.scale;

  return scene.tweens.add({
    targets: target,
    scale: baseScale * scaleRatio,
    duration,
    ease: "Sine.InOut",
    yoyo: true,
    repeat: -1,
    delay,
  });
}
